import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * HtcIntro — Homepage section introducing SAT-HTC as the inner OS
 * of the entire SAT Akademie. Kept compact — the full method lives on /sat-htc.
 */
const CODES_TAGS = [
  "Wesen", "Herkunft", "Name", "Schutz", "Schatten", "Herz",
  "Glauben", "Körper", "Beziehung", "Berufung", "Bewusstsein", "Vermächtnis",
];

const MOVEMENTS = ["Decode", "Feel", "Recode", "Embody", "Serve"];

export default function HtcIntro() {
  return (
    <section
      id="sat-htc"
      className="relative py-32 lg:py-40 bg-[#081328] overflow-hidden"
      data-testid="home-htc-intro"
    >
      {/* Ambient background */}
      <div className="absolute inset-0 z-0">
        <img
          src="/brand/htc/mastermatrix.png"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-[0.08] blur-[3px]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#081328] via-transparent to-[#081328]"></div>
      </div>

      <div className="container-sat relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: heading & method statement */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-[#D4AF37]"></span>
              <span className="overline">SAT-HTC™ · Human Transformation Code</span>
            </div>
            <h2 className="serif font-light leading-[1.05] text-4xl md:text-5xl lg:text-6xl">
              Du bist nicht falsch.
              <span className="block italic text-[#D4AF37] mt-2">Du bist verschlüsselt.</span>
            </h2>
            <p className="serif italic text-lg md:text-xl text-white/70 mt-8 leading-relaxed max-w-xl">
              Im Herzen aller SAT-Angebote wirkt der SAT-HTC — das von Nenad Vladović
              entwickelte Transformationssystem der SAT Akademie.
            </p>
            <div className="mt-10 space-y-3 text-white/75 text-[15px] leading-relaxed">
              <p>
                Der Mensch ist kein Problem, das repariert werden muss.
                Er ist ein lebendiger Code – aus Wesen, Prägung, Schutz, Schatten, Herz,
                Körper, Beziehung, Bewusstsein und Berufung.
              </p>
              <p className="pt-2 serif italic text-white/85">
                Vom Fremdcode zum Ursprungscode.
              </p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/sat-htc" className="btn-primary" data-testid="home-htc-cta-more">
                Mehr über SAT-HTC erfahren <ArrowRight size={16} />
              </Link>
              <Link to="/sat-master" className="btn-secondary" data-testid="home-htc-cta-master">
                Meinen Code erkennen
              </Link>
            </div>
          </div>

          {/* Right: compact visual — 12 codes as constellation + 5 movements as flow */}
          <div className="lg:col-span-6 lg:pl-8">
            <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-8 lg:p-10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-6">
                Die 12 Codes menschlicher Transformation
              </div>
              <div className="flex flex-wrap gap-2 mb-8">
                {CODES_TAGS.map((c, i) => (
                  <span
                    key={c}
                    className="text-[13px] px-3.5 py-1.5 rounded-full border border-[#D4AF37]/25 text-white/80 hover:border-[#D4AF37]/70 hover:text-[#D4AF37] transition cursor-default"
                    style={{ animationDelay: `${i * 60}ms` }}
                    data-testid={`home-htc-code-tag-${i}`}
                  >
                    {String(i + 1).padStart(2, "0")} · {c}
                  </span>
                ))}
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4 pt-4 border-t border-white/[0.06]">
                5 Bewegungen
              </div>
              <div className="flex flex-wrap items-center gap-2 text-white/85">
                {MOVEMENTS.map((m, i) => (
                  <React.Fragment key={m}>
                    <span className="serif italic text-lg">{m}</span>
                    {i < MOVEMENTS.length - 1 && (
                      <ArrowRight size={14} className="text-[#D4AF37]/60" />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/[0.06] text-center">
                <div className="serif italic text-[#D4AF37] text-lg">Love Is Transformation.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
